import { Component, OnInit } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { ViewClinicProductComponent } from '../../clinic-management/view-clinic-product/view-clinic-product.component';
import { CartService } from 'src/app/_services/cart.service';
import { MessageService } from 'primeng/api';
import { ClinicProductService } from 'src/app/_services/clinic-product.service';
import { ClinicService } from 'src/app/_services/clinic.service';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-clinic-home',
  templateUrl: './clinic-home.component.html',
  styleUrls: ['./clinic-home.component.scss'],
})
export class ClinicHomeComponent implements OnInit {
  searchTerm: any;
  disabled: boolean = false;
  constructor(
    public dialogService: DialogService,
    private cartService: CartService,
    private messageService: MessageService,
    private clinicProductService: ClinicProductService,
    private clinicService: ClinicService
  ) {}

  errorMessage: string = '';
  loadingPage: boolean = true;
  clinics: any[] = [];
  selectedClinic: any;
  clinicCount: number = 0;
  clinicProducts: any[] = [];
  // quantity options need ot go in sequential
  quantityOptions: number[] = [];
  currentPage: number = 1;
  selectedQuantity: number = 1;
  searchTermChanged = new Subject<string>();

  metaData = {
    totalRecords: 0,
    limit: 0,
    offset: 0,
    loading: true,
    globalFilter: '',
    sort: '',
    id: '',
  };

  ngOnInit(): void {
    this.searchTermChanged
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((term) => {
        this.filterProducts();
      });

    this.loadInitialData();
  }

  onSearchTermChange(event: any) {
    this.searchTermChanged.next(event.target.value);
  }

  loadInitialData(): void {
    this.loadClinics();
    this.loadProducts();
  }

  loadClinics(): void {
    this.clinicService.getAssignedClinics().subscribe(
      (clinics: any) => {
        this.clinics = clinics.body.data;
        console.log('Assigned Clinics:', clinics);
        
        this.clinicCount = clinics.body.count;

        if (this.clinicCount == 1) {
          this.selectedClinic = this.clinics[0];
        } else {
          this.selectedClinic = this.clinics[0];
        }
      },
      (error) => {
        console.error('Error fetching assigned clinics:', error);
        this.loadingPage = false;
      }
    );
  }

  loadProducts(): void {
    this.loadingPage = true;
    this.clinicProductService.getClinicProducts(this.metaData).subscribe(
      (products: any) => {
        console.log('Clinic Products:', products);
        this.clinicProducts = products.body.data;
        this.loadingPage = false;
        // map image URLs to the products
        this.clinicProducts = this.applyProductImages(this.clinicProducts);

        // if the clinic product has the description of Flyers black and white double sided A6 or Flyers colour double sided A6, change the quantity options to product.quantity
        if (this.clinicProducts.length > 0) {
          this.clinicProducts.forEach((product) => {
            if (
              product.description ===
                'Flyers black and white double sided A6' ||
              product.description === 'Flyers colour double sided A6'
            ) {

              // flyers: fixed quantity
              product.selectedQuantity = 1;
              product.disableQuantityDropdown = true; // custom flag to disable dropdown
            } else {
              // everything else: 1–100
              this.quantityOptions = Array.from(
                { length: 100 },
                (_, i) => i + 1
              );
              product.disableQuantityDropdown = false;
            }
          });
        }

        console.log('Mapped Clinic Products:', this.clinicProducts);

        this.metaData.totalRecords = products.body.totalRecords;
      },
      (error) => {
        console.error('Error fetching clinic products:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load clinic products',
          key: 'main',
        });
        this.loadingPage = false;
      }
    );
  }

  refreshProducts(): void {
    this.loadProducts();
    this.messageService.add({
      severity: 'info',
      summary: 'Refreshed',
      detail: 'Products refreshed successfully',
      key: 'main',
    });
  }

  onClinicChange() {
    console.log('Selected clinic:', this.selectedClinic);
    this.errorMessage = '';
    this.loadProducts(); // Reload products for the new clinic
  }

  onQuantityChange(product: any) {
    // Clear error state when quantity is selected
    if (product.selectedQuantity && product.selectedQuantity > 0) {
      product.showQuantityError = false;
    }
  }

  viewProduct(product: any) {
    const ref = this.dialogService.open(ViewClinicProductComponent, {
      header: 'Product Details',
      width: '75%',
      data: product,
    });

    ref.onClose.subscribe((result) => {
      if (result) {
        console.log('Product viewed:', result);
      }
    });
  }

  addProductToCart(product: any) {
    if (!this.selectedClinic) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select a clinic before adding products to the cart.',
        key: 'main',
      });
      this.errorMessage =
        'Please select a clinic before adding products to the cart.';

      return;
    }

    // Check if quantity is selected
    if (!product.selectedQuantity || product.selectedQuantity === 0) {
      // Set error state for this product
      product.showQuantityError = true;

      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          'Please select a quantity before adding this product to the cart.',
        key: 'main',
      });
      return;
    }

    // Clear any previous error state
    product.showQuantityError = false;

    let cart: {
      clinic_id?: string;
      stock_item_code: string;
      cost_excl_vat: number;
      cost_incl_vat?: number;
      name?: string;
      description?: string;
      product_id?: string;
      quantity: number;
      total_quantity: number;
      image_url?: string;
      clinic?: any;
    } = {
      clinic_id: this.selectedClinic?.id,
      product_id: product.id,
      stock_item_code: product.stock_item_code,
      name: product.name,
      description: product.description,
      cost_excl_vat: product.cost_excl_vat,
      cost_incl_vat: product.cost_incl_vat,
      quantity: product.selectedQuantity * product.quantity,
      total_quantity: product.selectedQuantity,
      image_url: product.image_url,
      clinic: this.selectedClinic,
    };

    const savedProduct = localStorage.getItem('sendToCart');
    if (savedProduct != null) {
      const cartItems = JSON.parse(savedProduct);
      cartItems.push(cart);
      localStorage.setItem('sendToCart', JSON.stringify(cartItems));
      this.cartService.updateCartItems(cartItems);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Product added to cart successfully',
        key: 'main',
      });
    } else {
      const cartItems = [];
      cartItems.push(cart);
      localStorage.setItem('sendToCart', JSON.stringify(cartItems));
      this.cartService.updateCartItems(cartItems);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Product added to cart successfully',
        key: 'main',
      });
    }
  }

  loadPreviousPage() {
    this.loadingPage = true;
    if (this.currentPage > 1) {
      this.currentPage -= 1;
      // change metaData offset to load previous page
      this.metaData.offset -= this.metaData.limit;
      this.clinicProductService
        .getClinicProducts(this.metaData)
        .subscribe((products: any) => {
          console.log('Clinic Products:', products);
          this.clinicProducts = products.body.data;
          this.metaData.totalRecords = products.body.totalRecords;
          this.loadingPage = false;
        });
    }
  }

  loadNextPage() {
    this.loadingPage = true;
    this.currentPage += 1;
    // change metaData offset to load next page
    this.metaData.offset += this.metaData.limit;
    this.clinicProductService
      .getClinicProducts(this.metaData)
      .subscribe((products: any) => {
        console.log('Clinic Products:', products);
        this.clinicProducts = products.body.data;
        this.metaData.totalRecords = products.body.totalRecords;
        this.loadingPage = false;
      });
  }

  applyProductImages(products: any[]): any[] {
    return products.map((product, index) => {
      // Assign a default image if none exists
      if (product.image_url) {
        product.image_url = `${environment.apiUrl}/${product.image_url}`;
      }
      return product;
    });
  }

  filterProducts() {
    this.loadingPage = true;
    this.metaData.offset = 0;
    this.currentPage = 1;
    this.metaData.globalFilter = this.searchTerm.trim();

    this.clinicProductService.getClinicProducts(this.metaData).subscribe(
      (products: any) => {
        const data = products.body.data;
        const total = products.body.totalRecords;

        this.clinicProducts = data;
        this.metaData.totalRecords = total;
        this.loadingPage = false;

        this.clinicProducts = this.applyProductImages(this.clinicProducts);

        if (this.clinicProducts.length > 0) {
          this.clinicProducts.forEach((product) => {
            if (
              product.description ===
                'Flyers black and white double sided A6' ||
              product.description === 'Flyers colour double sided A6'
            ) {
              // flyers: fixed quantity
              product.selectedQuantity = 1;
              product.disableQuantityDropdown = true; // custom flag to disable dropdown
            } else {
              // everything else: 1–100
              this.quantityOptions = Array.from(
                { length: 100 },
                (_, i) => i + 1
              );
              product.disableQuantityDropdown = false;
            }
          });
        }

        // Optional: Handle no results
        if (!data.length) {
          this.messageService.add({
            severity: 'info',
            summary: 'No Results',
            detail: 'No products match your search.',
            key: 'main',
          });
        }
      },
      (error) => {
        console.error('Error fetching filtered products:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load filtered products',
          key: 'main',
        });
        this.loadingPage = false;
      }
    );
  }
}
